import { AILightIcon, AISquareIcon, CheckSolidIcon, TimesSolidIcon } from '../../../../../assets/icons';
import { setRegenerateMultipleChoiceQuestionByAIModal } from '../../../../../store/workspace/slice';
import { MultipleChoiceQuestionDetail } from '../../../../../store/workspace/types';
import { editExamThunk } from '../../../../../store/workspace/thunks';
import { setNotification } from '../../../../../store/general/slice';
import { Dispatch, SetStateAction, useState } from 'react';
import TextArea from '../../../../../components/TextArea';
import DropDown from '../../../../../components/DropDown';
import { useDispatch } from '../../../../../store/store';
import { CodeBlock, dracula } from 'react-code-blocks';
import Button from '../../../../../components/Button';
import Modal from '../../../../../components/Modal';
import styles from './style.module.scss';

export interface ListItem {
    id: number;
    title: string;
};

const questionTypes = [
    { id: 2, title: 'General' },
    { id: 1, title: 'Technical' },
    { id: 2, title: 'Coding' },
];

const questionLevels = [
    { id: 1, title: 'Easy' },
    { id: 2, title: 'Medium' },
    { id: 3, title: 'Hard' },
];

interface Props {
    jobId: string | null;
    selectedIndex: number | null;
    setSelectedIndex: Dispatch<SetStateAction<number | null>>;
    setMultipleChoiceExamQuestions: Dispatch<SetStateAction<MultipleChoiceQuestionDetail[]>>;
}

type Step = 'Prompt' | 'Result';

export default function RegenerateByAI(props: Props) {
    const {
        jobId,
        selectedIndex,
        setSelectedIndex,
        setMultipleChoiceExamQuestions,
    } = props;

    const dispatch = useDispatch();
    const [prompt, setPrompt] = useState('');
    const [step, setStep] = useState<Step>('Prompt');
    const [result, setResult] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [promptError, setPromptError] = useState(false);
    const [questionType, setQuestionType] = useState<ListItem | null>(null);
    const [questionLevel, setQuestionLevel] = useState<ListItem | null>(null);
    const [questionTypeError, setQuestionTypeError] = useState(false);
    const [questionLevelError, setQuestionLevelError] = useState(false);

    const handleRegenerate = () => {
        if (step === 'Prompt') {
            if (!prompt || !questionType || !questionLevel) {
                !questionType ? setQuestionTypeError(true) : setQuestionTypeError(false);
                !questionLevel ? setQuestionLevelError(true) : setQuestionLevelError(false);
                !prompt ? setPromptError(true) : setPromptError(false);
                return;
            }

            setIsLoading(true);
            setPromptError(false);
            setQuestionTypeError(false);
            setQuestionLevelError(false);

            dispatch(editExamThunk({
                action: 'question',
                jobId: jobId,
                index: selectedIndex,
                level: questionLevel.title.toLowerCase(),
                type: questionType.title.toLowerCase(),
                prompt
            }))
                .unwrap()
                .then((res) => {
                    setResult(JSON.parse(res));
                    setStep('Result');
                })
                .catch(() => {
                    dispatch(setNotification(['Something went wrong,Try again...', 'error']));
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setMultipleChoiceExamQuestions(prev => prev.map((q, i) => i === selectedIndex ? {
                ...q,
                ...result,
                isEdited: true,
                question: result.description,
            } : q));

            setSelectedIndex(null);
            dispatch(setRegenerateMultipleChoiceQuestionByAIModal(false));
        }
    };

    return (
        <Modal
            size='md'
            title='AI regenerate'
            icon={<AISquareIcon />}
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setRegenerateMultipleChoiceQuestionByAIModal(false))}
        >
            {step === 'Prompt' && <>
                <div className={styles.question_types}>
                    <DropDown
                        idKey='id'
                        isRequired
                        titleKey='title'
                        label='Question type'
                        setItem={setQuestionType}
                        selectedItem={questionType}
                        hasError={questionTypeError}
                        dropDownList={questionTypes}
                    />
                    <DropDown
                        idKey='id'
                        isRequired
                        titleKey='title'
                        label='Question level'
                        setItem={setQuestionLevel}
                        selectedItem={questionLevel}
                        hasError={questionLevelError}
                        dropDownList={questionLevels}
                    />
                </div>
                <TextArea
                    isRequired
                    value={prompt}
                    maxLength={200}
                    onChange={setPrompt}
                    hasError={promptError}
                    label='Write your prompt'
                />
            </>}

            {step === 'Result' && <>
                <div className={styles.result}>
                    <div className={styles.result_detail}>
                        <div className={styles.result_detail_item}>
                            <span className={styles.result_detail_item_label}>Question type: </span>
                            <span className={styles.result_detail_item_value}>{result.type}</span>
                        </div>
                        <div className={styles.result_detail_item}>
                            <span className={styles.result_detail_item_label}>Question level: </span>
                            <span className={styles.result_detail_item_value}>{result.level}</span>
                        </div>
                        <div className={styles.result_detail_question}>
                            <span>Question</span>
                            {(() => {
                                const md = result.description || '';
                                // Regex to match code blocks and text:
                                // Match: (text before code block)? ```(lang)?\n(code)\n``` (text after code block)?
                                // We'll use split to break description into array and process.
                                // Split to array, keeping code blocks
                                // Matches: ```lang\ncode\n```
                                const parts = md.split(/(```[\s\S]*?```)/g).filter(Boolean);

                                return parts.map((part: string, idx: number) => {
                                    const codeBlockMatch = part.match(/^```([a-zA-Z]*)\n([\s\S]*?)\n?```$/);
                                    if (codeBlockMatch) {
                                        // codeBlockMatch[1] may be '', codeBlockMatch[2] is code part
                                        const lang = codeBlockMatch[1] || undefined;
                                        const code = codeBlockMatch[2];
                                        // CodeBlock should take language and code as props
                                        return (
                                            <CodeBlock
                                                key={idx}
                                                language={lang}
                                                text={code}
                                                showLineNumbers={true}
                                                theme={dracula}
                                            />

                                        );
                                    } else {
                                        // If the text part is not just whitespace, render it in <p>
                                        // But support multiline (e.g. multiple paragraphs)
                                        return part.split('\n\n').map((para, pIdx) => (
                                            para.trim() !== '' ? <p key={idx + '-' + pIdx}>{para}</p> : null
                                        ));
                                    }
                                });
                            })()}
                        </div>
                    </div>

                    <div className={styles.options}>
                        <span>Answers</span>
                        {result.choices.map((option: any, index: number) => (
                            <div className={styles.option}>
                                <span className={styles.option_index}>{index + 1}.</span>
                                <div className={styles.option_content}>
                                    {result.answer === index ? <CheckSolidIcon /> : <TimesSolidIcon />}
                                    <span>{option}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </>}

            <div className={styles.actions}>
                <Button
                    text='Cancel'
                    type='Outline-Dark'
                    className={styles.actions_btn}
                    onClick={() => dispatch(setRegenerateMultipleChoiceQuestionByAIModal(false))}
                />
                <Button
                    type='Primary'
                    iconPosition='Left'
                    loading={isLoading}
                    onClick={handleRegenerate}
                    className={styles.actions_btn}
                    icon={step === 'Prompt' && <AILightIcon />}
                    text={step === 'Prompt' ? 'Regenerate' : 'Replace'}
                />
            </div>
        </Modal>
    )
}
